import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import ImageScroller from '../../components/imagescroller';
import FileList from '../../components/fileViewer/list';

import localUtil from '../../lib/util';
import { sharedSalesEnabled } from '../../lib/crossCustomerSales';

function calendarAvailabilityImg(img) {
    if (typeof img !== 'object') {
        return undefined;
    }
    return img.s3LargeLink;
}

@observer
class CalendarAvailabilityView extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sessionid: new Date().getTime(),
            connectedEntries: [],
            loadingConnected: false,
        };
        this.mainContainer = null;
        this.saveTimeoutCurrent = null;
        this.saveTimeoutEntries = {};
    }

    loadAll = async (props = this.props) => {
        const { id } = props;
        const { calendarAvailabilityStore } = this.props.stores;
        calendarAvailabilityStore.updateKeyValue('calendarAvailability', {});
        await calendarAvailabilityStore.load(id, false, { addData: [] });

        // Load connected entries if ref exists
        const { calendarAvailability } = calendarAvailabilityStore;
        if (calendarAvailability && calendarAvailability.ref) {
            this.setState({ loadingConnected: true });
            const connectedEntries = await this.loadConnectedEntries(calendarAvailability.ref);
            this.setState({
                connectedEntries,
                loadingConnected: false,
            });
        }
    }

    loadConnectedEntries = async (ref) => {
        const response = await util.fetchApi('/api/calendaravailabilities/', { method: 'GET' }, { ref, limit: 1000 });
        return response?.data || [];
    }

    handleEntryTimeChange = (entryId, field, value) => {
        const { calendarAvailabilityStore } = this.props.stores;
        const { connectedEntries } = this.state;

        // Update local state immediately for responsive UI
        const updatedEntries = connectedEntries.map(entry => {
            if (entry.id === entryId) {
                const updatedEntry = { ...entry, [field]: value };
                // Update title to reflect new times
                const startTime = util.formatDate(field === 'start' ? value : entry.start, { hour: '2-digit', minute: '2-digit', hour12: false }, true);
                const endTime = util.formatDate(field === 'end' ? value : entry.end, { hour: '2-digit', minute: '2-digit', hour12: false }, true);
                updatedEntry.title = `${startTime} - ${endTime}`;
                return updatedEntry;
            }
            return entry;
        });
        this.setState({ connectedEntries: updatedEntries });

        // Debounce save to server (800ms)
        if (this.saveTimeoutEntries[entryId]) {
            clearTimeout(this.saveTimeoutEntries[entryId]);
        }
        this.saveTimeoutEntries[entryId] = setTimeout(async () => {
            const entry = connectedEntries.find(e => e.id === entryId);
            const startTime = util.formatDate(field === 'start' ? value : entry.start, { hour: '2-digit', minute: '2-digit', hour12: false }, true);
            const endTime = util.formatDate(field === 'end' ? value : entry.end, { hour: '2-digit', minute: '2-digit', hour12: false }, true);
            const newTitle = `${startTime} - ${endTime}`;
            await calendarAvailabilityStore.save({ [field]: value, title: newTitle }, entryId);
        }, 800);
    }

    deleteEntry = async (entryId) => {
        const { calendarAvailabilityStore } = this.props.stores;
        const { connectedEntries } = this.state;
        const { callback = () => {} } = this.props;

        // Mark as offline (soft delete)
        await calendarAvailabilityStore.saveField(entryId, 'offline', 1);

        // Update local state
        const updatedEntries = connectedEntries.filter(entry => entry.id !== entryId);
        this.setState({ connectedEntries: updatedEntries });

        // If all entries deleted, close drawer
        if (updatedEntries.length === 0) {
            callback({ delete: true, id: entryId });
        }
    }

    deleteAllConnected = async () => {
        const { calendarAvailabilityStore } = this.props.stores;
        const { connectedEntries } = this.state;
        const { callback = () => {} } = this.props;

        // Delete all entries
        for (const entry of connectedEntries) {
            await calendarAvailabilityStore.saveField(entry.id, 'offline', 1);
        }

        callback({ delete: true, deleteAll: true });
    }

    updateAllConnected = async () => {
        const { calendarAvailabilityStore } = this.props.stores;
        const { calendarAvailability } = calendarAvailabilityStore;
        const { connectedEntries } = this.state;

        // Get times from current entry
        const currentStart = new Date(calendarAvailability.start);
        const currentEnd = new Date(calendarAvailability.end);
        const startTimeStr = `${String(currentStart.getHours()).padStart(2, '0')}:${String(currentStart.getMinutes()).padStart(2, '0')}`;
        const endTimeStr = `${String(currentEnd.getHours()).padStart(2, '0')}:${String(currentEnd.getMinutes()).padStart(2, '0')}`;
        const newTitle = `${startTimeStr} - ${endTimeStr}`;

        // Update all connected entries with the same times
        const updatedEntries = [];
        for (const entry of connectedEntries) {
            const entryDate = util.isoDate(entry.start, false, false, true);
            const newStart = `${entryDate}T${startTimeStr}:00`;
            const newEnd = `${entryDate}T${endTimeStr}:00`;

            await calendarAvailabilityStore.save({ start: newStart, end: newEnd, title: newTitle }, entry.id);
            updatedEntries.push({ ...entry, start: newStart, end: newEnd, title: newTitle });
        }

        this.setState({ connectedEntries: updatedEntries });
    }

    handleCurrentEntryTimeChange = (field, value) => {
        const { calendarAvailabilityStore } = this.props.stores;
        const { calendarAvailability } = calendarAvailabilityStore;

        // Update title to reflect new times
        const startTime = util.formatDate(field === 'start' ? value : calendarAvailability.start, { hour: '2-digit', minute: '2-digit', hour12: false }, true);
        const endTime = util.formatDate(field === 'end' ? value : calendarAvailability.end, { hour: '2-digit', minute: '2-digit', hour12: false }, true);
        const newTitle = `${startTime} - ${endTime}`;

        // Update store immediately for responsive UI
        calendarAvailabilityStore.updateObjectKeyValue('calendarAvailability', field, value);
        calendarAvailabilityStore.updateObjectKeyValue('calendarAvailability', 'title', newTitle);

        // Debounce save to server (800ms)
        if (this.saveTimeoutCurrent) {
            clearTimeout(this.saveTimeoutCurrent);
        }
        this.saveTimeoutCurrent = setTimeout(async () => {
            await calendarAvailabilityStore.save({ [field]: value, title: newTitle }, calendarAvailability.id);
        }, 800);
    }

    editcalendarAvailability = () => {
		const { id, drawerLevel = 1, callback = () => {} } = this.props;
		const { appState, calendarAvailabilityStore } = this.props.stores;
        const { calendarAvailability, visitors, animals } = calendarAvailabilityStore;
		const { drawerHeightMedium } = appState;
		appState.openDrawer('editCalendarAvailability', {
			height: drawerHeightMedium,
            calendarType: calendarAvailability.calendarType,
            calendarId: calendarAvailability.calendarId,
			id,
            callback: event => {
                // console.log('editcalendarAvailability.callback');
                callback(event);
            },
		}, drawerLevel + 1);
	}

    loadAnimals = async visitorIds => {
        const { animalStore, saleStore } = this.props.stores;
        const response = await animalStore.load({
            query: {
                owner: visitorIds,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('visitorAnimals', response);
    }

    loadSales = async visitorIds => {
        const { saleStore, userStore } = this.props.stores;
        await saleStore.loadVisitorSales(visitorIds, {
            query: { today: 1 },
            myCustomers: sharedSalesEnabled(userStore),
        });
    }

    deleteBooking = async () => {
        const { drawerLevel, callback = () => {} } = this.props;
        const { appState, userStore, calendarAvailabilityStore, saleStore } = this.props.stores;
        // TODO: Add action for did not show!
        const { calendarAvailability } = calendarAvailabilityStore;
        if (calendarAvailability.id) {
            await calendarAvailabilityStore.saveField(calendarAvailability.id, 'offline', 1);
            calendarAvailabilityStore.updateField(calendarAvailability.id, 'offline', 1);
            callback({ delete: true, id: calendarAvailability.id, calendarId: calendarAvailability.calendarId});
        }
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.page !== this.props.page || nextProps.artid !== this.props.artid) {
            this.loadAll(nextProps);
        }
    }

    componentWillUnmount() {
        // Clear any pending save timeouts
        if (this.saveTimeoutCurrent) {
            clearTimeout(this.saveTimeoutCurrent);
        }
        Object.values(this.saveTimeoutEntries).forEach(timeout => {
            if (timeout) clearTimeout(timeout);
        });
    }

    render() {
        const { connectedEntries, loadingConnected } = this.state;
        const { userStore, calendarAvailabilityStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const { calendarAvailability } = calendarAvailabilityStore;

        if (!calendarAvailability) {
            // return loading spinner
            return (<>
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                        <div class='d-flex justify-content-center'>
                            <div class='spinner-border text-primary' role='status'>
                                <span class='sr-only'>Loading...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </>);
        }

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    <div class={`d-flex justify-content-start flex-row w-100`}>
                        <div class='flex-fill d-flex align-items-center ms-3'>
                            {calendarAvailability.category === 'onlineBooking' ? (
                                <span class='badge bg-info rounded-pill px-3 py-2'>
                                    <i class='fa-duotone fa-globe me-2' />
                                    <Text id='calendarAvailability.online-booking-rotation'>Online Booking</Text>
                                </span>
                            ) : (
                                <span class='badge bg-success rounded-pill px-3 py-2'>
                                    <i class='fa-duotone fa-calendar-check me-2' />
                                    <Text id='calendarAvailability.normal-rotation'>Normal Rotation</Text>
                                </span>
                            )}
                        </div>
                        <div class='' style='margin-right: 65px;'>
                            <button onClick={this.editcalendarAvailability} class={`btn btn-sm btn-outline-secondary rounded-pill me-2`}>
                                <i class={`fa-regular fa-pen me-2`} />
                                <Text id='calendar.event.edit'>Edit event</Text>
                            </button>
                        </div>
                    </div>

                    <div class='w-100 d-flex flex-column'>
                        <div class='px-0 box-header-info d-flex flex-column'>
                            <div class='d-flex'>
                                {/* {JSON.stringify(calendarAvailability)} */}
                                <ImageScroller images={calendarAvailability.images} stores={this.props.stores} showImg={img => calendarAvailabilityImg(img, this.props, '1920x')} showImageInfo={true} />
                            </div>

                            {calendarAvailability.files?.length > 0 && calendarAvailability.files.map(file => {
                                // console.log({ file });
                                if (file.s3Link.match(/\.(m4v|m4a|mp4)$/i)) {
                                    return (<>
                                        <div className='video-container rounded-lg'>
                                            <video controls className='video-player'>
                                                <source src={file.s3Link} type='video/mp4' />
                                                Your browser does not support the video tag.
                                            </video>
                                        </div>
                                        <div class='video-info d-flex flex-row justify-content-center'>
                                            <span class='text-muted'>size:</span> {localUtil.formatFileSize(file.size)}
                                            <span class='text-muted ms-3 me-1'>encoding:</span> {file.encoding}
                                            <span class='text-muted ms-3 me-1'>mimetype:</span> {file.mimetype}
                                            <span class='text-muted ms-3 me-1'>ext:</span> {file.ext}
                                            <span class='text-muted ms-3 me-1'>s3Link:</span> <a href={file.s3Link} target='_blank' rel='noopener noreferrer'>download</a>
                                        </div>
                                    </>);
                                }
                            })}

                            {calendarAvailability.didNotShow && <div class='d-flex flex-row justify-content-center'>
                                <span class='badge text-bg-danger rounded-pill px-3'><Text id='dyrejournal.calendar-event.did-not-show'>Did not show</Text></span>
                            </div>}

                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                                {/* Editable Start/End Times */}
                                {(() => {
                                    const startDate = calendarAvailability.start ? new Date(calendarAvailability.start) : null;
                                    const endDate = calendarAvailability.end ? new Date(calendarAvailability.end) : null;
                                    const startTimeStr = startDate ? `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}` : '';
                                    const endTimeStr = endDate ? `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}` : '';
                                    const dateStr = startDate ? util.formatDate(startDate, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', locale: 'nb-NO' }, true) : '';

                                    return (<>
                                        <div class='d-flex flex-row align-items-center mb-2'>
                                            <span class='text-muted font-weight-light me-2' style='min-width: 60px;'>
                                                <Text id='calendarAvailability.date'>Date</Text>
                                            </span>
                                            <span class='fw-bold'>{dateStr}</span>
                                        </div>
                                        <div class='d-flex flex-row align-items-center mb-2'>
                                            <span class='text-muted font-weight-light me-2' style='min-width: 60px;'>
                                                <Text id='calendarAvailability.start-time'>Start</Text>
                                            </span>
                                            <input
                                                type='time'
                                                class='form-control form-control-sm'
                                                style='max-width: 120px;'
                                                value={startTimeStr}
                                                onChange={e => {
                                                    const dateOnlyStr = util.isoDate(calendarAvailability.start, false, false, true);
                                                    const newStart = `${dateOnlyStr}T${e.target.value}:00`;
                                                    this.handleCurrentEntryTimeChange('start', newStart);
                                                }}
                                            />
                                        </div>
                                        <div class='d-flex flex-row align-items-center mb-2'>
                                            <span class='text-muted font-weight-light me-2' style='min-width: 60px;'>
                                                <Text id='calendarAvailability.end-time'>End</Text>
                                            </span>
                                            <input
                                                type='time'
                                                class='form-control form-control-sm'
                                                style='max-width: 120px;'
                                                value={endTimeStr}
                                                onChange={e => {
                                                    const dateOnlyStr = util.isoDate(calendarAvailability.end, false, false, true);
                                                    const newEnd = `${dateOnlyStr}T${e.target.value}:00`;
                                                    this.handleCurrentEntryTimeChange('end', newEnd);
                                                }}
                                            />
                                        </div>
                                    </>);
                                })()}
                            </div>


                            {/* Update all connected entries button */}
                            {connectedEntries.length > 1 && (
                                <div class='d-flex justify-content-center mx-3 mt-3'>
                                    <button
                                        type='button'
                                        class='btn btn-primary rounded-pill'
                                        onClick={this.updateAllConnected}
                                    >
                                        <i class='fa-solid fa-sync me-2' />
                                        <Text id='calendarAvailability.update-all-connected'>Update all connected entries</Text> ({connectedEntries.length})
                                    </button>
                                </div>
                            )}

                            {/* Connected Entries Section */}
                            {loadingConnected && (
                                <div class='d-flex justify-content-center my-3'>
                                    <div class='spinner-border spinner-border-sm text-primary' role='status'>
                                        <span class='sr-only'>Loading...</span>
                                    </div>
                                </div>
                            )}

                            {connectedEntries.length > 1 && (
                                <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                                    <div class='d-flex justify-content-between align-items-center mb-3'>
                                        <h6 class='mb-0'>
                                            <Text id='calendarAvailability.connected-entries'>Connected Entries</Text>
                                            <span class='badge text-bg-secondary ms-2'>{connectedEntries.length}</span>
                                        </h6>
                                    </div>

                                    <div class='table-responsive'>
                                        <table class='table table-sm table-bordered'>
                                            <thead>
                                                <tr>
                                                    <th><Text id='calendarAvailability.date'>Date</Text></th>
                                                    <th><Text id='calendarAvailability.start-time'>Start</Text></th>
                                                    <th><Text id='calendarAvailability.end-time'>End</Text></th>
                                                    <th><Text id='calendarAvailability.actions'>Actions</Text></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {connectedEntries
                                                    .sort((a, b) => new Date(a.start) - new Date(b.start))
                                                    .map(entry => {
                                                        const startDate = new Date(entry.start);
                                                        const endDate = new Date(entry.end);
                                                        const startTimeStr = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
                                                        const endTimeStr = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
                                                        return (
                                                            <tr key={entry.id}>
                                                                <td>
                                                                    {util.formatDate(entry.start, {
                                                                        weekday: 'short',
                                                                        day: '2-digit',
                                                                        month: 'short',
                                                                        locale: 'nb-NO'
                                                                    }, true)}
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type='time'
                                                                        class='form-control form-control-sm'
                                                                        value={startTimeStr}
                                                                        onChange={e => {
                                                                            const dateStr = util.isoDate(entry.start, false, false, true);
                                                                            const newStart = `${dateStr}T${e.target.value}:00`;
                                                                            this.handleEntryTimeChange(entry.id, 'start', newStart);
                                                                        }}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type='time'
                                                                        class='form-control form-control-sm'
                                                                        value={endTimeStr}
                                                                        onChange={e => {
                                                                            const dateStr = util.isoDate(entry.end, false, false, true);
                                                                            const newEnd = `${dateStr}T${e.target.value}:00`;
                                                                            this.handleEntryTimeChange(entry.id, 'end', newEnd);
                                                                        }}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        class='btn btn-sm btn-outline-danger'
                                                                        onClick={() => this.deleteEntry(entry.id)}
                                                                    >
                                                                        <i class='fa-solid fa-trash' />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>

                    {/* <SimpleBlog stores={this.props.stores} {...this.props} /> */}
                </div>
            </div>

            <div class='w-100 d-flex flex-row justify-content-center mt-3 px-4 py-3 bg-light fixed-bottom' style={`bottom: 0; left: 0;`}>
                {connectedEntries.length > 1 ? (
                    <button type='button' class='btn btn-outline-danger rounded-pill mx-2' onClick={this.deleteAllConnected}>
                        <Text id='dyrejournal.delete-all-availability'>Delete all</Text> ({connectedEntries.length})
                    </button>
                ) : (
                    <button type='button' class='btn btn-outline-danger rounded-pill mx-2' onClick={this.deleteBooking}>
                        <Text id='dyrejournal.delete-availability'>Delete availability</Text>
                    </button>
                )}
            </div>

        </>);
    }
}

export default CalendarAvailabilityView;
